import { type Action, createStore } from 'react-sweet-state';

import type { ReviewerProjectListItem } from 'src/data/project';
import { queryReviewerProjects } from 'src/graphql/project';
import { captureMessage } from 'src/lib/sentry';

export interface State {
  projects: ReviewerProjectListItem[];
  endCursor: string | null;
  hasNextPage: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
  loadMoreError: boolean;
}

export interface ContainerProps {
  customerId: string;
}

export type Actions = typeof ACTIONS;

type StoreAction<TResult = void> = Action<State, ContainerProps, TResult>;

const INITIAL_STATE: State = {
  projects: [],
  endCursor: null,
  hasNextPage: false,
  loading: true,
  loadingMore: false,
  error: false,
  loadMoreError: false,
};

export const PRIVATE_ACTIONS = {
  init:
    (): StoreAction =>
    ({ setState }) => {
      setState(INITIAL_STATE);
    },
};

export const ACTIONS = {
  loadProjects:
    (): StoreAction<Promise<void>> =>
    async ({ setState }, { customerId }) => {
      setState({ loading: true, error: false });
      try {
        const { projects, pageInfo } = await queryReviewerProjects({ customerId });
        setState({
          projects,
          endCursor: pageInfo.endCursor ?? null,
          hasNextPage: pageInfo.hasNextPage,
        });
      } catch (error) {
        captureMessage('Failed to load the projects shared for review', {
          level: 'error',
          extra: { error, customerId },
        });
        setState({ error: true });
      } finally {
        setState({ loading: false });
      }
    },

  loadMoreProjects:
    (): StoreAction<Promise<void>> =>
    async ({ getState, setState }, { customerId }) => {
      const { endCursor, hasNextPage, loadingMore } = getState();
      if (!hasNextPage || loadingMore) {
        return;
      }

      setState({ loadingMore: true, loadMoreError: false });
      try {
        const { projects, pageInfo } = await queryReviewerProjects({
          customerId,
          after: endCursor,
        });
        setState({
          projects: [...getState().projects, ...projects],
          endCursor: pageInfo.endCursor ?? null,
          hasNextPage: pageInfo.hasNextPage,
        });
      } catch (error) {
        captureMessage('Failed to load more projects shared for review', {
          level: 'error',
          extra: { error, customerId },
        });
        // The scroll trigger asks again as soon as a load ends, so a failed page stops the paging.
        setState({ hasNextPage: false, loadMoreError: true });
      } finally {
        setState({ loadingMore: false });
      }
    },
};

export const Store = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'ReviewerDashboardPage',
});
